import { useCallback, useEffect, useRef, useState } from "react";
import { api, errMsg } from "../lib/api";

export const isBusy = (v) => !!v && (v.clips.some((c) => c.status === "generating") || v.music?.status === "generating" || v.export?.status === "rendering");

export const useVideo = (vid) => {
  const [video, setVideo] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    try { setVideo(await api.video(vid)); setError(null); } catch (e) { setError(errMsg(e)); }
  }, [vid]);

  useEffect(() => { setVideo(null); setError(null); load(); }, [load]);
  const busy = isBusy(video);
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [busy, load]);

  return { video, setVideo, error, busy };
};

export const clipCost = (pricing, quality, duration) => {
  const rate = pricing?.[quality === "fast" ? "fast_per_sec" : "cinematic_per_sec"] ?? 0;
  return rate > 0 ? Math.ceil(rate * duration) : 0;
};

export const EMPTY_CLIP = { prompt: "", photo: null, quality: "cinematic", duration: 5 };

export const ASPECT_BOX = { "16:9": "aspect-video w-full max-w-[818px]", "9:16": "aspect-[9/16] h-full max-w-full", "1:1": "aspect-square w-full max-w-[420px]" };

export const secs = (n) => `${Number(n || 0).toFixed(1).replace(/\.0$/, "")}s`;

// Same rules as the server render (core/video_media.py): emoji dropped, tiny trims ignored.
export const cleanCaption = (t) => (t || "").replace(/[\u{1F000}-\u{1FAFF}\u2600-\u27BF\uFE0F\u200D]/gu, "").trim();

export const stripCaps = (rows) => rows.map(({ id, text, start, end, position }) => ({ id, text, start: Number(start) || 0, end: Number(end) || 0, position }));

export const useCaptionDraft = (video) => {
  const server = JSON.stringify(stripCaps(video?.captions || []));
  const [rows, setRows] = useState([]);
  useEffect(() => { setRows(JSON.parse(server)); }, [server]);
  return { rows, setRows, dirty: JSON.stringify(stripCaps(rows)) !== server };
};

// Only one player makes sound at a time: claim() pauses every other preview/music/final player.
export const useSolo = (name, pause) => {
  const ref = useRef(pause);
  ref.current = pause;
  useEffect(() => {
    const h = (e) => { if (e.detail !== name) ref.current(); };
    window.addEventListener("vs-play", h);
    return () => window.removeEventListener("vs-play", h);
  }, [name]);
  return useCallback(() => window.dispatchEvent(new CustomEvent("vs-play", { detail: name })), [name]);
};

export const buildSegments = (clips) => {
  let offset = 0;
  return clips.filter((c) => c.status === "ready" && c.video_url).map((c) => {
    const d = Number(c.duration || 0);
    let s = Math.max(0, Number(c.trim_start || 0));
    let e = Math.min(d, Number(c.trim_end || d));
    if (e - s < 0.3) { s = 0; e = d; }
    const seg = { id: c.id, url: c.video_url, s, e, len: e - s, offset };
    offset += e - s;
    return seg;
  });
};
